import React, { useState, useEffect } from 'react';
import { Globe, X } from 'lucide-react';

const DISMISS_KEY_PREFIX = 'mif:dismiss_country_nudge:';

/**
 * Dismissible banner shown only to users whose `country` field is null/empty.
 * Deep-links to Settings → Profile where they can set it via the 195-country dropdown.
 *
 * Dismiss state is per-user and persists in localStorage. Cleared automatically
 * once the user actually sets their country (no longer null in the user prop).
 */
export const CountryNudgeBanner = ({ user, onGoToSettings }) => {
  const [dismissed, setDismissed] = useState(false);
  const dismissKey = user?.user_id ? `${DISMISS_KEY_PREFIX}${user.user_id}` : null;

  useEffect(() => {
    if (!dismissKey) return;
    setDismissed(localStorage.getItem(dismissKey) === '1');
  }, [dismissKey]);

  const hasCountry = !!(user?.country && String(user.country).trim());
  if (hasCountry || dismissed || !user) return null;

  const handleDismiss = () => {
    if (dismissKey) localStorage.setItem(dismissKey, '1');
    setDismissed(true);
  };

  return (
    <div
      className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4 flex items-start gap-3"
      data-testid="country-nudge-banner"
    >
      <div className="shrink-0 mt-0.5 bg-amber-100 p-1.5 rounded">
        <Globe className="w-4 h-4 text-amber-700" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-amber-900 font-medium">
          We don&apos;t know your country yet
        </p>
        <p className="text-xs text-amber-800 mt-0.5">
          Setting your country unlocks the right regional pricing and personalises your dashboard. It takes 5 seconds.
        </p>
        <button
          onClick={onGoToSettings}
          className="mt-2 text-xs font-medium text-amber-900 underline underline-offset-2 hover:text-amber-700"
          data-testid="country-nudge-go-to-settings"
        >
          Set my country in Settings →
        </button>
      </div>
      <button
        onClick={handleDismiss}
        className="shrink-0 text-amber-700 hover:text-amber-900 p-1"
        title="Dismiss for now"
        data-testid="country-nudge-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default CountryNudgeBanner;
